/**
 * CR108: Multi-example JSON picker for Rule Packs
 * Provides deterministic and hybrid examples per pack type
 */

import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { FileJson, ChevronDown, Sparkles, Cog } from "lucide-react";
import type { PackType } from "@/hooks/useRulePacks";

// ================================================================
// EXAMPLE CONFIGURATIONS
// ================================================================

export const EXAMPLE_NORMALIZE_DETERMINISTIC = {
  schema_version: "1.0.0",
  deal_type_allowlist: ["BONUS", "DISCOUNT", "POINTS", "IN_KIND"],
  validation_flags: {
    require_merchant_key: true,
    require_dates: false,
    require_value: false,
  },
  field_precedence: {
    merchant_key: ["merchant_key", "merchant_name"],
    title: ["headline", "title", "raw_text"],
    description: ["description", "body_snippet", "raw_text"],
  },
  title_formatting: {
    max_length: 120,
    strip_merchant_prefix: true,
    capitalize_first: true,
  },
};

export const EXAMPLE_NORMALIZE_HYBRID = {
  schema_version: "1.0.0",
  deal_type_allowlist: ["BONUS", "DISCOUNT", "POINTS", "IN_KIND"],
  validation_flags: {
    require_merchant_key: true,
    require_dates: false,
    require_value: false,
  },
  field_precedence: {
    merchant_key: ["merchant_key", "merchant_name"],
    title: ["headline", "title", "raw_text"],
  },
  ai: {
    enabled: false,
    provider: "gemini-2.5-flash-lite",
    prompt_pack_key: "NORMALIZE_AI_V1",
    fallback_to_deterministic: true,
  },
};

export const EXAMPLE_DEDUPE_DETERMINISTIC = {
  schema_version: "1.0.0",
  mode: "deterministic",
  // CR158: Include signature and brands.purchase_brand to prevent collapsing variant exploder outputs
  dedupe_key_fields: ["merchant_key", "deal_type", "signature", "brands.purchase_brand", "headline", "start_date", "end_date"],
  similarity_threshold: 0.85,
  source_priority: {
    merchant_website: 100,
    flyer: 80,
    forum: 60,
    community: 50,
  },
  hard_rules: {
    never_merge_different_promo_merchants: true,
    never_merge_different_purchase_brands_when_both_present: true,
    require_same_deal_type: true,
    require_overlapping_dates: true,
  },
  tolerances: {
    amount_tolerance_cad: 1.0,
    percent_tolerance: 1.0,
    date_tolerance_days: 1,
  },
};

export const EXAMPLE_DEDUPE_HYBRID = {
  schema_version: "1.0.0",
  mode: "hybrid",
  // CR158: Include signature and brands.purchase_brand to prevent collapsing variant exploder outputs
  dedupe_key_fields: ["merchant_key", "deal_type", "signature", "brands.purchase_brand", "headline", "start_date", "end_date"],
  similarity_threshold: 0.75,
  source_priority: {
    merchant_website: 100,
    flyer: 80,
    forum: 60,
    community: 50,
  },
  hard_rules: {
    never_merge_different_promo_merchants: true,
    never_merge_different_purchase_brands_when_both_present: true,
    require_same_deal_type: true,
  },
  tolerances: {
    amount_tolerance_cad: 2.0,
    percent_tolerance: 2.0,
  },
  ai: {
    enabled: false,
    provider: "gemini-2.5-flash-lite",
    prompt_pack_key: "DEDUP_AI_V1",
    confidence_threshold: 0.85,
    max_pairs: 200,
  },
};

// ================================================================
// COMPONENT
// ================================================================

interface ExampleJsonPickerProps {
  packType: PackType;
  onSelect: (json: Record<string, unknown>) => void;
}

export function ExampleJsonPicker({ packType, onSelect }: ExampleJsonPickerProps) {
  const examples = packType === "NORMALIZE"
    ? [
        {
          label: "Deterministic Example",
          description: "Field precedence, validation flags, title formatting",
          icon: Cog,
          config: EXAMPLE_NORMALIZE_DETERMINISTIC,
        },
        {
          label: "Hybrid AI Assist Example",
          description: "Includes AI block (disabled by default)",
          icon: Sparkles,
          config: EXAMPLE_NORMALIZE_HYBRID,
        },
      ]
    : [
        {
          label: "Deterministic Example",
          description: "Similarity thresholds, hard rules, tolerances",
          icon: Cog,
          config: EXAMPLE_DEDUPE_DETERMINISTIC,
        },
        {
          label: "Hybrid AI Assist Example",
          description: "Includes AI block for fuzzy matching (disabled by default)",
          icon: Sparkles,
          config: EXAMPLE_DEDUPE_HYBRID,
        },
      ];

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="outline" size="sm" className="text-xs">
          <FileJson className="h-3 w-3 mr-1" />
          Load Example JSON
          <ChevronDown className="h-3 w-3 ml-1" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel className="text-xs">
          {packType === "NORMALIZE" ? "Normalize Pack Examples" : "Dedup Pack Examples"}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {examples.map((example, idx) => (
          <DropdownMenuItem
            key={idx}
            onClick={() => onSelect(example.config)}
            className="flex flex-col items-start gap-0.5 cursor-pointer"
          >
            <div className="flex items-center gap-2 font-medium text-sm">
              <example.icon className="h-3.5 w-3.5" />
              {example.label}
            </div>
            <span className="text-xs text-muted-foreground pl-5">
              {example.description}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
